import clsx from 'clsx'
import { ChevronRight, Folder } from '@/assets/icons'
import { Button } from '../button/Button'
import { Tag } from '../tag/Tag'

export interface SampleMatterCardProps {
  /** Main title text */
  title: string
  /** Secondary text below the title */
  description: string
  /** Tag label shown next to the title (e.g. "Sample Matter"). Omit to hide the tag. */
  tag?: string
  /** Header banner text shown above the content. Omit to hide the banner. */
  headerBannerText?: string
  /** Action button shown at the bottom of the card. Omit to hide the button. */
  button?: {
    /** Button label (e.g. "Explore") */
    label: string
    /** Click handler for the button */
    onClick?: () => void
  }
  /** Additional class names applied to the card root */
  className?: string
}

/**
 * SampleMatterCard
 *
 * A showcase card with an optional blue header banner, a title with an optional
 * tag, a description, and an optional outlined action button. Used to surface
 * pre-built sample cases.
 */
export function SampleMatterCard({ title, description, tag, headerBannerText, button, className }: SampleMatterCardProps) {
  return (
    <div className={clsx('flex w-full flex-col overflow-hidden rounded-lg bg-white', className)}>
      {headerBannerText && (
        <div className="flex w-full items-start gap-1.5 rounded-t-lg border border-brand-100 bg-brand-50 px-4 py-2">
          <Folder className="mt-[0.5px] h-3.5 w-3.5 shrink-0 text-brand-800" aria-hidden />
          <p className="min-w-0 flex-1 text-xs font-medium leading-4 text-brand-800">{headerBannerText}</p>
        </div>
      )}

      <div
        className={clsx(
          'flex w-full flex-col items-start gap-4 border-x border-b border-brand-neutral-200 px-6 py-4',
          headerBannerText ? 'rounded-b-lg' : 'rounded-lg border-t'
        )}
      >
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1">
            <p className="min-w-0 break-words font-rethink-sans text-base font-medium leading-6 text-brand-neutral-950">{title}</p>
            {tag && (
              <Tag variant="tonal" color="Green" size="xs">
                {tag}
              </Tag>
            )}
          </div>
          <p className="w-full text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-600">{description}</p>
        </div>

        {button && (
          <Button variant="outlined" color="primary" size="sm" trailingIcon={<ChevronRight className="h-4 w-4" />} onClick={button.onClick}>
            {button.label}
          </Button>
        )}
      </div>
    </div>
  )
}
